import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Newspaper, ExternalLink, RefreshCw, Clock } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import useSeo from '../hooks/useSeo';
import CTA from '../components/CTA';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

function ArticleThumb({ item, iconSize = 34 }) {
  const [imgOk, setImgOk] = React.useState(!!item.image);
  if (imgOk) {
    return (
      <img
        src={item.image}
        alt=""
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        loading="lazy"
        onError={() => setImgOk(false)}
      />
    );
  }
  // Branded fallback: publisher favicon on a subtle gradient tile
  return (
    <div className="relative w-full h-full grid place-items-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="absolute inset-0 grid-bg opacity-60" />
      <div className="relative flex flex-col items-center gap-2 px-4 text-center">
        {item.favicon ? (
          <span className="w-14 h-14 rounded-2xl bg-white grid place-items-center p-2.5 shadow-lg">
            <img src={item.favicon} alt={item.source} className="max-w-full max-h-full object-contain" loading="lazy" />
          </span>
        ) : (
          <Newspaper size={iconSize} className="text-slate-600" />
        )}
        <span className="font-display text-sm font-semibold text-slate-600">{item.source}</span>
      </div>
    </div>
  );
}

export default function Blog() {
  useSeo({ title: 'Insights | Marketing & Performance Marketing News | Interscope Ads', description: 'Daily marketing, performance marketing, mobile UA and adtech news aggregated from top publications worldwide.' });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('All');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/blog`);
      setItems(res.data.items || []);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sources = useMemo(() => ['All', ...Array.from(new Set(items.map((i) => i.source)))], [items]);
  const list = source === 'All' ? items : items.filter((i) => i.source === source);
  const featured = list[0];
  const rest = list.slice(1);
  useReveal(`${source}:${list.length}`);

  return (
    <main className="pt-32">
      <section className="relative max-w-7xl mx-auto px-5 md:px-8 pb-6 text-center">
        <span className="reveal inline-flex items-center gap-2 mb-3 text-xs font-semibold tracking-[0.25em] uppercase text-[#2563eb]"><Newspaper size={14} /> Insights</span>
        <h1 className="reveal font-display text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.08] max-w-3xl mx-auto">
          Marketing & <span className="text-gradient">performance</span> news
        </h1>
        <p className="reveal mt-6 text-slate-600 text-lg max-w-2xl mx-auto">
          The latest in performance marketing, mobile UA and adtech — aggregated daily from leading publications worldwide.
        </p>
      </section>

      {/* Source filters */}
      <section className="relative max-w-7xl mx-auto px-5 md:px-8 pt-6">
        <div className="flex flex-wrap items-center justify-center gap-2.5 mb-10">
          {sources.map((s) => (
            <button key={s} onClick={() => setSource(s)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${source === s ? 'bg-gradient-to-r from-[#2563eb] to-[#1d4ed8] text-white' : 'glass text-slate-600 hover:text-blue-600 hover:border-[#2563eb]/40'}`}>
              {s}
            </button>
          ))}
          <button onClick={load} className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-sm font-medium text-slate-600 hover:border-[#2563eb]/50 transition-colors">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>

        {loading && items.length === 0 ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="glass rounded-2xl h-72 animate-pulse" />
            ))}
          </div>
        ) : list.length === 0 ? (
          <div className="glass rounded-3xl p-16 text-center text-slate-500">No articles available right now. Please try refreshing.</div>
        ) : (
          <>
            {/* Featured */}
            {featured && (
              <a href={featured.url} target="_blank" rel="noreferrer" className="reveal group block rounded-3xl overflow-hidden glass glass-hover mb-8">
                <div className="grid md:grid-cols-2">
                  <div className="relative h-56 md:h-full min-h-[220px] overflow-hidden bg-slate-50">
                    <ArticleThumb item={featured} iconSize={48} />
                  </div>
                  <div className="p-7 md:p-9 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-3 text-xs">
                      <span className="rounded-full bg-[#2563eb]/12 px-3 py-1 font-medium text-[#2563eb] border border-[#2563eb]/30">{featured.source}</span>
                      <span className="inline-flex items-center gap-1 text-slate-500"><Clock size={12} /> {timeAgo(featured.published)}</span>
                    </div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 leading-tight group-hover:text-[#2563eb] transition-colors">{featured.title}</h2>
                    <p className="mt-3 text-slate-500 leading-relaxed">{featured.summary}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2563eb]">Read on {featured.source} <ExternalLink size={15} /></span>
                  </div>
                </div>
              </a>
            )}

            {/* Grid */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {rest.map((a, i) => (
                <a key={i} href={a.url} target="_blank" rel="noreferrer" className="reveal group flex flex-col rounded-2xl overflow-hidden glass glass-hover">
                  <div className="relative h-44 overflow-hidden bg-slate-50">
                    <ArticleThumb item={a} iconSize={34} />
                    <span className="absolute top-3 left-3 rounded-full bg-black/60 backdrop-blur px-3 py-1 text-[11px] font-medium text-white border border-white/25">{a.source}</span>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-center gap-1 text-[11px] text-slate-500 mb-2"><Clock size={11} /> {timeAgo(a.published)}</div>
                    <h3 className="font-display text-base font-semibold text-slate-900 leading-snug group-hover:text-[#2563eb] transition-colors line-clamp-3">{a.title}</h3>
                    <p className="mt-2 text-sm text-slate-500 leading-relaxed line-clamp-3 flex-1">{a.summary}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#2563eb]">Read more <ExternalLink size={13} /></span>
                  </div>
                </a>
              ))}
            </div>
            <p className="text-center text-xs text-slate-600 mt-8">Headlines & summaries are aggregated from third-party publications and link to the original source. Updated hourly.</p>
          </>
        )}
      </section>

      <CTA />
    </main>
  );
}
